import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Public: lets the pricing page show real plans without any credentials. */
export const fetchCheckoutPlans = createServerFn({ method: "GET" }).handler(async () => {
  const { listActivePlans } = await import("./billing.server");
  const { isCashfreeConfigured } = await import("./cashfree.server");
  const plans = await listActivePlans();
  return { plans, gatewayReady: isCashfreeConfigured() };
});

const checkoutInput = z.object({
  plan_id: z.string().trim().min(1).max(40),
  simulation_addon_selected: z.boolean().default(false),
  payment_type: z.enum(["new_subscription", "renewal", "simulation_addon"]),
  phone: z
    .string()
    .trim()
    .regex(/^[0-9]{10}$/)
    .optional(),
});

/**
 * Creates the payment request (server-priced) and its Cashfree order.
 * Requires an authenticated, non-guest user; the secret key never leaves here
 * and the browser only receives the payment session id.
 */
export const startCashfreeCheckout = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => checkoutInput.parse(input))
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const { createPendingAddonRequest, createPendingPaymentRequest } = await import(
      "./billing.server"
    );
    const { ensureCashfreeOrder, readCashfreeConfig, readRequestRow, requirePayingUser } =
      await import("./cashfree.server");

    const user = await requirePayingUser();

    const cfg = readCashfreeConfig();

    // The credit add-on is a one-time pack: it is billed against the caller's
    // own active subscription and never touches plan, dates or status.
    const created =
      data.payment_type === "simulation_addon"
        ? await (async () => {
            const { resolveAddonPlan } = await import("./subscription.server");
            const plan = await resolveAddonPlan(user.id);
            if (!plan) throw new Error("NO_ACTIVE_SUBSCRIPTION");
            return createPendingAddonRequest({
              userId: user.id,
              email: user.email,
              planId: plan.planId,
            });
          })()
        : await createPendingPaymentRequest({
            userId: user.id,
            email: user.email,
            planId: data.plan_id,
            simulationAddonSelected: data.simulation_addon_selected,
            paymentType: data.payment_type,
          });

    const request = await readRequestRow(created.paymentRequestId);
    if (!request) throw new Error("REQUEST_NOT_FOUND");

    const origin =
      getRequestHeader("origin") ??
      (getRequestHeader("host") ? `https://${getRequestHeader("host")}` : "");
    if (!origin) throw new Error("ORIGIN_UNAVAILABLE");

    const order = await ensureCashfreeOrder({
      cfg,
      request,
      customerEmail: user.email,
      customerName: user.fullName,
      // Cashfree requires a phone; a placeholder keeps checkout usable when the
      // account has none. Real numbers are passed straight through.
      customerPhone: data.phone ?? "9999999999",
      origin,
      note:
        data.payment_type === "simulation_addon"
          ? "+300 Simulations add-on — The Quantum Mind"
          : `${created.planName} — The Quantum Mind`,
    });

    return {
      paymentRequestId: created.paymentRequestId,
      planId: created.planId,
      planName: created.planName,
      amountPaise: created.finalAmountPaise,
      orderId: order.orderId,
      paymentSessionId: order.paymentSessionId,
      mode: cfg.mode,
    };
  });

/**
 * Authoritative status for the return screen. Re-reads the order from
 * Cashfree server-side — simply landing back on the site never grants premium.
 */
export const fetchCheckoutStatus = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ order_id: z.string().trim().min(4).max(80) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { readAuthUser } = await import("./session.server");
    const { readEntitlement } = await import("./subscription.server");
    const { readCashfreeConfig, readRequestByOrderId, syncOrderStatus } = await import(
      "./cashfree.server"
    );

    const user = await readAuthUser();
    if (!user) throw new Error("NOT_AUTHENTICATED");

    const request = await readRequestByOrderId(data.order_id);
    // Ownership check: an order id guessed from someone else reveals nothing.
    if (!request || request.user_id !== user.id) throw new Error("ORDER_NOT_FOUND");

    const cfg = readCashfreeConfig();
    const synced = await syncOrderStatus({ cfg, request });
    const entitlement = await readEntitlement(user.id, user.isAnonymous);

    return {
      orderId: data.order_id,
      status: synced.status,
      amountPaise: request.final_amount_paise,
      planId: request.plan_id,
      entitlement,
    };
  });
